import { Injectable, inject } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { environment } from '../../environments/environment'
import { Dictionary } from '../shared/components/filtering-search-component-base/filtering-search-types'
import { Store } from '@ngrx/store'
import { Observable, of, switchMap } from 'rxjs'
import { MenuDefinition, selectAllMenus, selectListingState, ListingState, loadMenus, resetMegaMenus, resetArticleState } from '../state'
import { logout } from '../state/user.actions'
import { ToastService } from './toast.service'
import { MixpanelService } from './mixpanel.service'
import { User } from '../state/user.model'
import { selectUser } from '../state/user.reducer'
import { FeatueFlagsService } from './featue-flags.service'
import { Router } from '@angular/router'

@Injectable({
    providedIn: 'root'
})

export class BackEndService {
    private http = inject(HttpClient)
    store = inject<Store<any>>(Store)
    private toastService = inject(ToastService)
    private mixPanelService = inject(MixpanelService)
    private featureFlagsService = inject(FeatueFlagsService)
    private router = inject(Router)

    public menus$: Observable<MenuDefinition[]>
    public listingState$: Observable<ListingState>
    public user$: Observable<User | null>

    constructor() {
        this.menus$ = this.store.select(selectAllMenus)
        this.listingState$ = this.store.select(selectListingState)
        this.user$ = this.store.select(selectUser)
    }

    private postToHTTP(url: string, args: Dictionary<any>, includeMenus = true) {
        let menus: MenuDefinition[] = []

        if (includeMenus) {
            this.menus$.subscribe(m => menus = m)
        }

        return this.http.post(url, {
            ...args,
            menus: menus.map(m => ({ id: m.id, version: m.version, area: m.area })),
            featureflags: this.featureFlagsService.getFeatureFlags()
        }, {
            withCredentials: true
        }).pipe(
            switchMap((response: any) => {
                if (response?.loggedIn) {
                    if (response.messages) {
                        for (const message of response.messages) {
                            this.toastService.show(message.text, message.options)
                        }
                    }
                    if (response.menus.length > 0) {
                        this.store.dispatch(loadMenus({ menus: response.menus }))
                    }
                    if (response.closed) {
                        this.router.navigate(['/maintenance'])
                    }

                    return of(response)
                } else {
                    this.mixPanelService.reset()
                    this.store.dispatch(resetMegaMenus())
                    this.store.dispatch(resetArticleState())
                    this.store.dispatch(logout({ error: 'Logged Out' }))
                    return of({})
                }
            })
        )
    }


    article(area: string, id: number, args: Dictionary<any> = {}) {
        return this.postToHTTP(environment.baseUrl + area + '/' + id, args)
    }

    search(search: Dictionary<any>, includeMenus = true) {
        return this.postToHTTP(environment.baseUrl + search['area'], search, includeMenus)
    }

    shareByEmail(area: string, emailAddresses: string[], message: string, articleIDs: string[]) {
        return this.postToHTTP(environment.baseUrl + area + '/share', {
            parameters: {
                emailAddresses,
                message,
                articleIDs,
                origin: window.document.location.origin
            }
        })
    }

    createSavedSearch(savedSearch: Dictionary<any>) {
        return this.postToHTTP(environment.baseUrl + 'user/savedsearches/create', savedSearch).pipe(
            switchMap((response: any) => {
                if (response.user && response.ok) {
                    this.store.dispatch({ type: '[User] Update User', user: response.user })
                }
                return of(response)
            })
        )
    }
    editSavedSearch(id: string, savedSearch: Dictionary<any>) {
        return this.postToHTTP(environment.baseUrl + 'user/savedsearches/' + id, savedSearch).pipe(
            switchMap((response: any) => {
                if (response.user && response.ok) {
                    this.store.dispatch({ type: '[User] Update User', user: response.user })
                }
                return of(response)
            })
        )
    }

    getDocumentAnnotations(id: string) {
        return this.http.get(environment.baseUrl + 'documents/annotations/' + id, {
            withCredentials: true
        })
    }

    deleteSavedSearch(id: string) {
        return this.http.delete(environment.baseUrl + 'user/savedsearches/' + id, {
            withCredentials: true
        }).pipe(
            switchMap((response: any) => {
                if (response.user && response.ok) {
                    this.store.dispatch({ type: '[User] Update User', user: response.user })
                }
                return of(response)
            })
        )
    }

    download(search: Dictionary<any>) {
        return this.postToHTTP(environment.baseUrl + search['area'] + '/download', search)
    }

    downloadprogress(reference: string) {
        return this.http.get(environment.baseUrl + `download/${reference}`, {
            withCredentials: true
        })
    }

    checkJobStatus(token: string) {
        return this.http.get(environment.baseUrl + `job/${token}`, {
            withCredentials: true
        })
    }

    sendMessage(message: Dictionary<any>) {
        return this.http.post(environment.baseUrl + `instruments/send-message`, message, {
            withCredentials: true
        })
    }

    pacerLogin(pacerUsername: string, pacerPassword: string) {
        return this.postToHTTP(environment.baseUrl + 'pacer/login', {
            pacerUsername,
            pacerPassword,
        })
    }
    startDocumentProcess(request: any) {
        return this.postToHTTP(environment.baseUrl + 'pacer/purchase', request)
    }
    startDocketUpdateProcess(request: any) {
        return this.postToHTTP(environment.baseUrl + 'pacer/docketupdate', request)
    }

    validatePacerToken(token: string) {
        return this.postToHTTP(environment.baseUrl + 'pacer/validate', { token })
    }

    updateHasSeen(label: string) {
        return this.postToHTTP(environment.baseUrl + 'user/hasSeen', { label })
    }

    updateHomePageSections(sections: string[]) {
        return this.postToHTTP(environment.baseUrl + 'user/homepage', { sections })
    }
    getSubscriptions() {
        return this.http.get(environment.baseUrl + 'user/subscriptions', {
            withCredentials: true
        })
    }
    getEmailDeliveries() {
        return this.http.get(environment.baseUrl + 'user/email-deliveries', {
            withCredentials: true
        })
    }

    getAPIKey() {
        return this.http.get(environment.baseUrl + 'user/apikey', {
            withCredentials: true
        })
    }

    updateSubscriptions(subscriptions: Dictionary<any>) {
        return this.postToHTTP(environment.baseUrl + 'user/subscriptions', subscriptions)
    }
    sendFeedback(feedback: Dictionary<any>) {
        return this.postToHTTP(environment.baseUrl + 'user/feedback', feedback)
    }
    changePassword(passwordModel: Dictionary<any>) {
        return this.postToHTTP(environment.baseUrl + 'user/password', passwordModel)
    }

    sendPasswordChange() {
        return this.http.get(environment.baseUrl + 'user/passwordchange', {
            withCredentials: true
        })
    }

    validatePublicHash(hash: string = '') {
        return this.http.post(environment.baseUrl + 'validate-public' , {hash}, {
            withCredentials: true
        })
    }

    getUnsubscribeInformation(hash: string = '') {
        return this.http.post(environment.baseUrl + 'unsubscribe-info' , {hash}, {
            withCredentials: true
        })
    }
    unsubscribe(hash: string = '') {
        return this.http.post(environment.baseUrl + 'unsubscribe' , {hash}, {
            withCredentials: true
        })
    }

    getSECDocument(formID: number, docID: number) {
        return this.http.get(environment.baseUrl + `secdocuments/${formID}/${docID}`, {
            withCredentials: true,
        })
    }

    getSECURL(url: string) {
        return this.http.post(environment.baseUrl + `secdocuments`, {url}, {
            withCredentials: true,
        })
    }

    getCreditorPositions(entityID: number, caseIDs: number[]) {
        return this.http.post(environment.baseUrl + `data/creditors2/${entityID}`, caseIDs, {
            withCredentials: true,
        })
    }

    loadChatHistory(areaAndID: any) {
        return this.postToHTTP(environment.baseUrl + 'ai/history', areaAndID);
    }

    getDockets(id: number) {
        return this.http.get(environment.baseUrl + `documents/list/${id}`, {
            withCredentials: true,
        })
    }

    clearChatHistory(areaAndID: any) {
        return this.postToHTTP(environment.baseUrl + 'ai/clear-history', areaAndID);
    }

    addChatHistoryFeedback(historyID: string, feedback: 'positive' | 'negative', message: string) {
        return this.postToHTTP(environment.baseUrl + 'ai/feedback', { historyID, feedback, message });
    }

    startAIProcess(request: any) {
        return this.postToHTTP(environment.baseUrl + 'ai/start', {request})
    }

    checkAIProcesses(tokens: any) {
        return this.postToHTTP(environment.baseUrl + 'ai/status', {tokens})
    }

    getUsageOverview(search: Dictionary<any>) {
        return this.postToHTTP(environment.baseUrl + 'my-account/usage/page-views/overview', search, false)
    }

}